import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { convertToDefEventPara } from "../../shared/utility";

const selectFieldPt = {
  root: { className: "w-full min-w-0 max-w-full" },
};

const dropdownPt = {
  ...selectFieldPt,
  input: { className: "text-sm" },
  label: { className: "min-w-0 flex-1 truncate text-sm" },
};

const multiSelectPt = {
  ...selectFieldPt,
  label: { className: "text-sm truncate" },
};

function resolveOption(value, options) {
  if (value == null || typeof value === "object") {
    return value;
  }
  const match = options.find((option) => option.value === value);
  return match ?? { value, label: String(value) };
}

/** Accepts legacy `{ value, label }`, scalars, or arrays of either. */
export function normalizePrimeSelectValue(value, isMulti) {
  if (isMulti) {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((item) =>
      typeof item === "object" && item != null && "value" in item
        ? item.value
        : item,
    );
  }

  if (value == null) {
    return null;
  }

  if (typeof value === "object" && "value" in value) {
    return value.value;
  }

  return value;
}

function toLegacyValue(nextValue, isMulti, options) {
  if (isMulti) {
    const values = Array.isArray(nextValue) ? nextValue : [];
    return values.map((item) => resolveOption(item, options));
  }

  return resolveOption(nextValue, options);
}

export default function SelectMenu(props) {
  const {
    error,
    handleInputChange,
    onValueChange,
    menuPortalTarget,
    name,
    options = [],
    defaultValue,
    isMulti,
    title,
    value,
    isClearable,
    disabled,
    parentClass,
    placeholder,
    panelClassName,
    panelStyle,
    filter,
    maxSelectedLabels = 3,
    multiDisplay = "comma",
    useOptionValue = true,
    primeProps = {},
  } = props;

  const rawValue = value ?? defaultValue ?? (isMulti ? [] : null);
  const selectedValue = useOptionValue
    ? normalizePrimeSelectValue(rawValue, isMulti)
    : rawValue;
  const appendTo =
    menuPortalTarget ??
    (typeof document !== "undefined" ? document.body : undefined);
  const resolvedPlaceholder = placeholder ?? "Select an option";
  const emptyMessage = "No options";

  const notifyChange = (nextValue) => {
    if (onValueChange) {
      onValueChange(nextValue);
    }
    if (handleInputChange) {
      const legacyValue = useOptionValue
        ? toLegacyValue(nextValue, isMulti, options)
        : nextValue;
      handleInputChange(convertToDefEventPara(name, legacyValue));
    }
  };

  const enableFilter = filter ?? options.length > 8;
  const filterPanelClassName = ["qatrace-filter-select-panel", panelClassName]
    .filter(Boolean)
    .join(" ");

  const panelProps = {
    appendTo,
    panelClassName: appendTo ? filterPanelClassName : panelClassName,
    panelStyle: appendTo
      ? { maxWidth: "20rem", width: "100%", ...panelStyle }
      : panelStyle,
  };

  const optionProps = useOptionValue
    ? { optionLabel: "label", optionValue: "value" }
    : { optionLabel: "label" };

  return (
    <div className={parentClass}>
      {title ? (
        <div className="mb-1 flex space-x-2">
          <label
            htmlFor={name}
            className="block text-sm font-medium text-[#0F172A]"
          >
            {title}
          </label>
        </div>
      ) : null}
      <div className="w-full min-w-0">
        {isMulti ? (
          <MultiSelect
            id={name}
            name={name}
            value={selectedValue}
            options={options}
            {...optionProps}
            onChange={(e) => notifyChange(e.value)}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            showClear={false}
            className="w-full"
            pt={multiSelectPt}
            display={multiDisplay}
            filter={enableFilter}
            filterPlaceholder="Search..."
            emptyMessage={emptyMessage}
            maxSelectedLabels={maxSelectedLabels}
            {...panelProps}
            {...primeProps}
          />
        ) : (
          <Dropdown
            id={name}
            name={name}
            value={selectedValue}
            options={options}
            {...optionProps}
            onChange={(e) => notifyChange(e.value)}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            showClear={!!isClearable}
            className="w-full max-w-full"
            pt={dropdownPt}
            filter={enableFilter}
            filterPlaceholder="Search..."
            emptyMessage={emptyMessage}
            {...panelProps}
            {...primeProps}
          />
        )}
        {error ? (
          <p className="mt-1 text-xs font-medium text-[#DC2626]">{error}</p>
        ) : null}
      </div>
    </div>
  );
}
